import { PasswordStrength, User } from "../../../../domain/entities/entities";
import { authenticationUseCases } from "../../../../domain/usecase/impl/authenticationUseCases";
import { resetStore } from "../store";
import { ActionContext } from "vuex";

class AuthenticationState {
  user: User = new User();
  isAdmin: boolean = false;
  isManager: boolean = false;

  /** Tells whether the password is visible or not. */
  isPasswordVisible: boolean = false;

  /** Entropy of the password. */
  passwordStrength: PasswordStrength = new PasswordStrength();
}

const state = new AuthenticationState();

const mutations = {
  USER_IS_ADMIN(state: AuthenticationState, isAdmin: boolean): void {
    state.isAdmin = isAdmin;
  },
  USER_IS_MANAGER(state: AuthenticationState, isManager: boolean): void {
    state.isManager = isManager;
  },
  USER_IS_STUDENT(state: AuthenticationState): void {
    state.isAdmin = false;
    state.isManager = false;
  },

  /** Mutation responsible for setting the passwordStrength state to password strength.
   * @param state
   * @param passwordStrength result of the user's password strength. */
  SET_PASSWORD(
    state: AuthenticationState,
    passwordStrength: PasswordStrength
  ): void {
    state.passwordStrength = passwordStrength;
  },

  /** Mutation responsible for showing the password in the input. */
  SHOW_PASSWORD(state: AuthenticationState) {
    state.isPasswordVisible = true;
  },

  /** Mutation responsible for hiding the password in the input. */
  HIDE_PASSWORD(state: AuthenticationState) {
    state.isPasswordVisible = false;
  }
};

const actions = {
  async login(
    ctx: ActionContext<AuthenticationState, AuthenticationState>,
    user: User
  ): Promise<boolean> {
    return await authenticationUseCases.login(user);
    //await ctx.dispatch("getUserType");
    // return loginRequestOutcome;
  },

  async finishOnBoarding(
    { commit }: ActionContext<AuthenticationState, AuthenticationState>,
    user: User
  ): Promise<void> {
    await authenticationUseCases.finishOnBoarding(user);
  },

  async registerNewUser(
    { commit }: ActionContext<AuthenticationState, AuthenticationState>,
    user: User
  ): Promise<void> {
    await authenticationUseCases.register(user);
  },

  async getUserType({
    commit
  }: ActionContext<AuthenticationState, AuthenticationState>): Promise<void> {
    const userType = await authenticationUseCases.getUserType();
    switch (userType) {
      case 1:
        commit("USER_IS_STUDENT");
        break;
      case 2:
        commit("USER_IS_ADMIN", true);
        break;
      case 4:
        commit("USER_IS_MANAGER", true);
        break;
      default:
        commit("USER_IS_STUDENT");
    }
  },

  async logout(): Promise<void> {
    await resetStore();
    await authenticationUseCases.logout();
  },

  /** Action responsible for resetting password strength.
   * @param commit
   * @param password password entered by the user. */
  async validatePasswordStrength(
    { commit }: ActionContext<AuthenticationState, AuthenticationState>,
    password: string
  ): Promise<void> {
    const response: PasswordStrength = await authenticationUseCases.validatePasswordStrength(
      password
    );
    commit("SET_PASSWORD", response);
  },

  async acceptTerm({
    commit
  }: ActionContext<AuthenticationState, AuthenticationState>): Promise<void> {
    try {
      await authenticationUseCases.acceptTerm();
    } catch (e) {
      console.error(e);
      throw e;
    }
  },

  /** Action responsible for clearing the passwordStrength state. */
  clearPasswordStrength({
    commit
  }: ActionContext<AuthenticationState, AuthenticationState>): void {
    commit("SET_PASSWORD", new PasswordStrength());
  },

  /** Action responsible for showing the password in the input. */
  showPassword({ commit }): void {
    commit("SHOW_PASSWORD");
  },

  /** Action responsible for hiding the password in the input. */
  hidePassword({ commit }): void {
    commit("HIDE_PASSWORD");
  }
};

const getters = {
  isAdmin: (state): boolean => state.isAdmin,
  isManager: (state): boolean => state.isManager,

  /** Gets the current state of the password (whether it is visible or not). */
  isPasswordVisible: (state): boolean => state.isPasswordVisible,

  /** Password strength getter. */
  passwordStrength: (state): PasswordStrength => state.passwordStrength
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
